import { useState } from "react";
import { useNavigate } from "react-router-dom";

type Step = "email" | "otp" | "newPassword" | "success";

export default function ForgotPasswordPage() {
    const navigate = useNavigate();
    const [step, setStep] = useState<Step>("email");
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const handleSendOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        await new Promise((r) => setTimeout(r, 800));
        setLoading(false);
        setStep("otp");
    };

    const handleVerifyOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        const code = otp.join("");
        if (code.length < 6) {
            setError("Vui lòng nhập đủ 6 chữ số.");
            return;
        }
        setLoading(true);
        await new Promise((r) => setTimeout(r, 700));
        setLoading(false);
        if (code === "123456") {
            setStep("newPassword");
        } else {
            setError("Mã OTP không đúng. Vui lòng thử lại.");
        }
    };

    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");
        if (newPassword.length < 6) {
            setError("Mật khẩu phải có ít nhất 6 ký tự.");
            return;
        }
        if (newPassword !== confirmPassword) {
            setError("Mật khẩu xác nhận không khớp.");
            return;
        }
        setLoading(true);
        await new Promise((r) => setTimeout(r, 800));
        setLoading(false);
        setStep("success");
    };

    const handleOtpChange = (index: number, value: string) => {
        if (!/^\d*$/.test(value)) return;
        const next = [...otp];
        next[index] = value.slice(-1);
        setOtp(next);
        if (value && index < 5) {
            const nextInput = document.getElementById(`otp-${index + 1}`);
            nextInput?.focus();
        }
    };

    const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            const prevInput = document.getElementById(`otp-${index - 1}`);
            prevInput?.focus();
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex flex-col items-center justify-center px-6 py-10">
            {/* Logo */}
            <div className="flex flex-col items-center mb-8">
                <div className="w-24 h-24 rounded-2xl overflow-hidden mb-4 shadow-md">
                    <img
                        src="https://smt.dlhf.vn/storage/general/smt-app-favicon200.png"
                        alt="Dalat Hasfarm Logo"
                        className="w-full h-full object-contain bg-white"
                    />
                </div>
                <h1 className="text-2xl font-bold text-green-800 tracking-tight">Dalat Hasfarm</h1>
                <p className="text-sm text-gray-500 mt-1 font-medium">Hệ Thống Quản Lý Tồn Kho</p>
            </div>

            {/* Card */}
            <div className="w-full max-w-sm bg-white rounded-3xl shadow-lg p-8">
                {/* Back button */}
                {step !== "success" && (
                    <button
                        onClick={() => (step === "email" ? navigate("/") : setStep(step === "otp" ? "email" : "otp"))}
                        className="flex items-center gap-1.5 text-sm text-gray-500 font-medium cursor-pointer mb-5 hover:text-gray-700 transition-colors"
                    >
                        <i className="ri-arrow-left-line text-base"></i>
                        {step === "email" ? "Quay lại đăng nhập" : "Quay lại"}
                    </button>
                )}

                {/* Step: Email */}
                {step === "email" && (
                    <>
                        <div className="mb-6">
                            <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-orange-50 mb-4">
                                <i className="ri-lock-password-line text-2xl text-orange-500"></i>
                            </div>
                            <h2 className="text-xl font-bold text-gray-800">Quên mật khẩu?</h2>
                            <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">
                                Nhập email tài khoản của bạn, chúng tôi sẽ gửi mã xác nhận để đặt lại mật khẩu.
                            </p>
                        </div>
                        <form onSubmit={handleSendOtp} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                                <div className="relative">
                                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                                        <i className="ri-mail-line text-lg"></i>
                                    </span>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="email@hasfarm.com"
                                        required
                                        className="w-full pl-10 pr-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all"
                                    />
                                </div>
                            </div>
                            {error && (
                                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                                    <i className="ri-error-warning-line text-red-500"></i>
                                    <p className="text-sm text-red-600">{error}</p>
                                </div>
                            )}
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-gradient-to-r from-green-600 to-green-500 text-white font-bold py-3.5 rounded-xl text-base shadow-md active:scale-95 transition-all cursor-pointer whitespace-nowrap disabled:opacity-70 mt-2"
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <i className="ri-loader-4-line animate-spin"></i> Đang gửi...
                                    </span>
                                ) : (
                                    "Gửi mã xác nhận"
                                )}
                            </button>
                        </form>
                    </>
                )}

                {/* Step: OTP */}
                {step === "otp" && (
                    <>
                        <div className="mb-6">
                            <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-green-50 mb-4">
                                <i className="ri-message-2-line text-2xl text-green-600"></i>
                            </div>
                            <h2 className="text-xl font-bold text-gray-800">Nhập mã OTP</h2>
                            <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">
                                Mã xác nhận 6 chữ số đã được gửi đến{" "}
                                <span className="font-semibold text-gray-700">{email}</span>
                            </p>
                        </div>
                        <form onSubmit={handleVerifyOtp} className="space-y-5">
                            <div className="flex gap-2 justify-between">
                                {otp.map((digit, i) => (
                                    <input
                                        key={i}
                                        id={`otp-${i}`}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleOtpChange(i, e.target.value)}
                                        onKeyDown={(e) => handleOtpKeyDown(i, e)}
                                        className="w-11 h-12 text-center text-lg font-bold border-2 border-gray-200 rounded-xl focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all"
                                    />
                                ))}
                            </div>
                            {error && (
                                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                                    <i className="ri-error-warning-line text-red-500"></i>
                                    <p className="text-sm text-red-600">{error}</p>
                                </div>
                            )}
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-gradient-to-r from-green-600 to-green-500 text-white font-bold py-3.5 rounded-xl text-base shadow-md active:scale-95 transition-all cursor-pointer whitespace-nowrap disabled:opacity-70"
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <i className="ri-loader-4-line animate-spin"></i> Đang xác nhận...
                                    </span>
                                ) : (
                                    "Xác nhận"
                                )}
                            </button>
                            <div className="text-center">
                                <button
                                    type="button"
                                    onClick={() => setStep("email")}
                                    className="text-sm text-orange-500 font-medium cursor-pointer"
                                >
                                    Gửi lại mã
                                </button>
                            </div>
                        </form>
                        <div className="mt-4 p-3 bg-green-50 rounded-xl">
                            <p className="text-xs text-green-700 font-medium mb-1">Demo OTP:</p>
                            <p className="text-xs text-green-600">
                                Nhập <strong>123456</strong> để tiếp tục
                            </p>
                        </div>
                    </>
                )}

                {/* Step: New Password */}
                {step === "newPassword" && (
                    <>
                        <div className="mb-6">
                            <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-green-50 mb-4">
                                <i className="ri-shield-keyhole-line text-2xl text-green-600"></i>
                            </div>
                            <h2 className="text-xl font-bold text-gray-800">Đặt mật khẩu mới</h2>
                            <p className="text-sm text-gray-500 mt-1.5">Mật khẩu phải có ít nhất 6 ký tự.</p>
                        </div>
                        <form onSubmit={handleResetPassword} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">Mật khẩu mới</label>
                                <div className="relative">
                                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                                        <i className="ri-lock-line text-lg"></i>
                                    </span>
                                    <input
                                        type={showNew ? "text" : "password"}
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="••••••••"
                                        required
                                        className="w-full pl-10 pr-12 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNew(!showNew)}
                                        className="absolute right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 cursor-pointer"
                                    >
                                        <i className={showNew ? "ri-eye-off-line text-lg" : "ri-eye-line text-lg"}></i>
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Xác nhận mật khẩu
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                                        <i className="ri-lock-2-line text-lg"></i>
                                    </span>
                                    <input
                                        type={showConfirm ? "text" : "password"}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="••••••••"
                                        required
                                        className="w-full pl-10 pr-12 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirm(!showConfirm)}
                                        className="absolute right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 cursor-pointer"
                                    >
                                        <i
                                            className={showConfirm ? "ri-eye-off-line text-lg" : "ri-eye-line text-lg"}
                                        ></i>
                                    </button>
                                </div>
                            </div>
                            {error && (
                                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                                    <i className="ri-error-warning-line text-red-500"></i>
                                    <p className="text-sm text-red-600">{error}</p>
                                </div>
                            )}
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-gradient-to-r from-green-600 to-green-500 text-white font-bold py-3.5 rounded-xl text-base shadow-md active:scale-95 transition-all cursor-pointer whitespace-nowrap disabled:opacity-70 mt-2"
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <i className="ri-loader-4-line animate-spin"></i> Đang lưu...
                                    </span>
                                ) : (
                                    "Đặt lại mật khẩu"
                                )}
                            </button>
                        </form>
                    </>
                )}

                {/* Step: Success */}
                {step === "success" && (
                    <div className="flex flex-col items-center text-center py-4">
                        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mb-5">
                            <i className="ri-checkbox-circle-line text-4xl text-green-600"></i>
                        </div>
                        <h2 className="text-xl font-bold text-gray-800 mb-2">Đặt lại thành công!</h2>
                        <p className="text-sm text-gray-500 leading-relaxed mb-8">
                            Mật khẩu của bạn đã được cập nhật. Hãy đăng nhập lại với mật khẩu mới.
                        </p>
                        <button
                            onClick={() => navigate("/")}
                            className="w-full bg-gradient-to-r from-green-600 to-green-500 text-white font-bold py-3.5 rounded-xl text-base shadow-md active:scale-95 transition-all cursor-pointer whitespace-nowrap"
                        >
                            Về trang đăng nhập
                        </button>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="mt-8 text-center">
                <p className="text-xs text-gray-400">© 2026 Dalat Hasfarm. Phiên bản 1.0.0</p>
            </div>
        </div>
    );
}
